import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { formatNum, can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

export default function Transfers() {
  const { user } = useAuth();
  const editable = can(user.role, "transfers") || user.role === "admin" || user.role === "inventory_manager";
  const [rows, setRows] = useState([]);
  const [raws, setRaws] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { item_type: "raw", item_id: "", from_warehouse_id: "", to_warehouse_id: "", quantity: 1 };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [t, r, p, w] = await Promise.all([
      api.get("/transfers"), api.get("/raw-materials"), api.get("/products"), api.get("/warehouses"),
    ]);
    setRows(t.data); setRaws(r.data); setProducts(p.data); setWarehouses(w.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const whName = (id) => warehouses.find((w) => w.id === id)?.name || "—";

  const rawOptions = useMemo(
    () => raws.filter((r) => r.warehouse_id === form.from_warehouse_id),
    [raws, form.from_warehouse_id]
  );
  const finishedStock = (pid, wid) => {
    const p = products.find((x) => x.id === pid);
    const s = (p?.stock_by_warehouse || []).find((x) => x.warehouse_id === wid);
    return s ? s.quantity : 0;
  };

  const openNew = () => { setForm({ ...empty, from_warehouse_id: warehouses[0]?.id || "", to_warehouse_id: warehouses[1]?.id || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.item_id) return toast.error("Select an item");
    try { await api.post("/transfers", form); toast.success("Stock transferred"); setOpen(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const itemLabel = (row) => row.item_name || row.item_id;

  return (
    <div data-testid="transfers-page">
      <PageHeader title="Stock Transfers" subtitle="Move raw materials & finished goods between warehouses"
        action={editable && (
          <Button className="rounded-none gap-2" data-testid="create-transfer-btn" onClick={openNew} disabled={warehouses.length < 2}>
            <Plus className="w-4 h-4" /> New Transfer
          </Button>
        )} />

      {warehouses.length < 2 && (
        <div className="border border-border bg-secondary/40 p-3 text-sm text-muted-foreground mb-4">You need at least two warehouses to transfer stock.</div>
      )}

      {rows.length === 0 ? <EmptyState title="No transfers yet" /> : (
        <div className="border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-secondary">
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Item</TableHead>
              <TableHead>From</TableHead><TableHead>To</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>By</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} className="hover:bg-secondary/60" data-testid="transfer-row">
                  <TableCell className="font-mono text-sm">{(row.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell><span className="text-xs font-mono uppercase bg-secondary px-2 py-0.5">{row.item_type}</span></TableCell>
                  <TableCell className="font-medium">{itemLabel(row)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{whName(row.from_warehouse_id)}</TableCell>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1"><ArrowRightLeft className="w-3 h-3" />{whName(row.to_warehouse_id)}</span>
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatNum(row.quantity)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{row.created_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display">New Transfer</DialogTitle>
            <DialogDescription>Move stock from one warehouse to another. Raw materials and finished products are supported.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Item type</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm((f) => ({ ...f, item_type: v, item_id: "" }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="transfer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="raw">Raw Material</SelectItem><SelectItem value="finished">Finished Product</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Quantity</Label><Input type="number" step="any" min="0.01" className="rounded-none mt-1" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: parseFloat(e.target.value || 0) }))} data-testid="transfer-qty-input" /></div>
            <div><Label>From warehouse</Label>
              <Select value={form.from_warehouse_id} onValueChange={(v) => setForm((f) => ({ ...f, from_warehouse_id: v, item_id: "" }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="transfer-from-select"><SelectValue /></SelectTrigger>
                <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>To warehouse</Label>
              <Select value={form.to_warehouse_id} onValueChange={(v) => setForm((f) => ({ ...f, to_warehouse_id: v }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="transfer-to-select"><SelectValue /></SelectTrigger>
                <SelectContent>{warehouses.filter((w) => w.id !== form.from_warehouse_id).map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Item</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm((f) => ({ ...f, item_id: v }))}>
                <SelectTrigger className="rounded-none mt-1" data-testid="transfer-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {form.item_type === "raw"
                    ? rawOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} — {formatNum(r.quantity)} {r.unit}</SelectItem>)
                    : products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {formatNum(finishedStock(p.id, form.from_warehouse_id), 0)} in stock</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="sm:col-span-2"><Button type="submit" className="rounded-none" data-testid="save-transfer-btn">Transfer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
